import { useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Copy, KeyRound, RotateCw, Send, Trash2, XCircle } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from '@/stores/toast-store';
import { useProductsPosConfig } from '@/hooks/use-pos-config';
import {
  currentMonth,
  useCampaignApiClients,
  useCampaignApiStats,
  useCreateCampaignApiClient,
  useRevokeCampaignApiClient,
  useRotateCampaignApiClient,
  type CampaignApiClientWithSecret,
  type CampaignApiStatsCounts,
} from '@/hooks/use-campaign-api';
import { DEFAULT_TIME_ZONE } from '@/lib/report-timezone';

// Super-admin only, per location (Campaign API design v0.3 §11). A credential
// lets a store's own integration text and email that store's customers, from
// the store's campaign numbers — a platform decision, handed over by support,
// never something a business issues for itself. Everything a send needs comes
// from the location: the Customer Engagement switch and the campaign number
// pool above. There is no sender to set up here.

const message = (e: unknown, fallback: string) => (e as { error?: { message?: string } })?.error?.message || fallback;

export function CampaignApiCredentialsRow({
  tenantId,
  locationId,
  timeZone = DEFAULT_TIME_ZONE,
}: {
  tenantId: string;
  locationId: string;
  // The store's time zone: months in the usage table follow store time.
  timeZone?: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: posConfig } = useProductsPosConfig(locationId, tenantId);
  const engagementOn = posConfig?.customerEngagementEnabled === true;
  const pool = posConfig?.campaignSmsNumbers?.length
    ? posConfig.campaignSmsNumbers
    : posConfig?.campaignSmsNumber
      ? [posConfig.campaignSmsNumber]
      : [];

  return (
    <div>
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Campaign API</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Credentials for this store's own integration to send SMS and email through the API, one customer at a time or
          a list of up to 100, from the campaign senders above. Customers it sends to are added to this business's
          customer list and share its opt-out list. Super admin only.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            <Readiness ok={engagementOn} label="Customer Management on" />
            <Readiness ok={pool.length > 0} label={`${pool.length} campaign sender${pool.length === 1 ? '' : 's'}`} />
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium"
          >
            <KeyRound className="h-3.5 w-3.5" />
            {open ? 'Hide credentials' : 'Manage credentials'}
          </button>
        </div>
        {!engagementOn || pool.length === 0 ? (
          <p className="mt-2 text-[11px] text-amber-700">
            {!engagementOn
              ? 'Sends are refused while Customer Management is off at this location.'
              : 'SMS sends are refused until a campaign sender is assigned.'}{' '}
            Credentials can still be issued for testing.
          </p>
        ) : null}
        {open && <CredentialsPanel tenantId={tenantId} locationId={locationId} timeZone={timeZone} />}
      </div>
    </div>
  );
}

function Readiness({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? 'text-green-700' : 'text-muted-foreground'}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function CredentialsPanel({
  tenantId,
  locationId,
  timeZone,
}: {
  tenantId: string;
  locationId: string;
  timeZone: string;
}) {
  const { data: clients, isLoading, error } = useCampaignApiClients(tenantId, locationId);
  const create = useCreateCampaignApiClient(tenantId, locationId);
  const rotate = useRotateCampaignApiClient(tenantId, locationId);
  const revoke = useRevokeCampaignApiClient(tenantId, locationId);

  const [label, setLabel] = useState('');
  const [env, setEnv] = useState<'test' | 'live'>('test');
  const [secret, setSecret] = useState<{ client: CampaignApiClientWithSecret; rotated: boolean } | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const list = clients?.data ?? [];

  return (
    <div className="mt-3 space-y-3 border-t border-border pt-3">
      {secret && <SecretOnce client={secret.client} rotated={secret.rotated} onDismiss={() => setSecret(null)} />}

      <div className="rounded-md border border-border bg-white p-3">
        <p className="mb-1 text-xs font-medium">Issue a credential</p>
        <p className="mb-2 text-[11px] text-muted-foreground">
          Start with Test, hand it over through a secure channel, and issue Live once their integration works. Their
          system exchanges it for a 15-minute token at <code>/campaign-api/v1/oauth/token</code>.
        </p>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Who is this for? e.g. Store website"
          maxLength={64}
          className="mb-2 w-full rounded-md border border-border p-2 text-sm"
        />
        <div className="mb-2 flex gap-2">
          {(['test', 'live'] as const).map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEnv(e)}
              className={`flex-1 rounded-md border-2 p-2 text-left text-xs ${env === e ? 'border-primary bg-primary/5' : 'border-border'}`}
            >
              <span className="font-medium">{e === 'test' ? 'Test' : 'Live'}</span>
              <span className="ml-2 text-muted-foreground">
                {e === 'test' ? 'mock provider, never billed' : "texts and emails this store's real customers"}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={!label.trim() || create.isPending}
          onClick={async () => {
            try {
              const client = await create.mutateAsync({ label: label.trim(), env });
              setSecret({ client, rotated: false });
              setLabel('');
            } catch (e) {
              toast.error(message(e, 'Could not issue the credential'));
            }
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          <KeyRound className="h-3.5 w-3.5" /> Issue {env} credential
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {message(error, 'Could not load credentials from Campaign API')}
        </p>
      ) : list.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          No credentials issued for this location yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-white">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="p-2 font-medium">Label</th>
                <th className="p-2 font-medium">Client ID</th>
                <th className="p-2 font-medium">Env</th>
                <th className="p-2 font-medium">Last used</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0">
                  <td className="p-2 font-medium">{c.label ?? '—'}</td>
                  <td className="p-2 font-mono text-[11px]">{c.client_id}</td>
                  <td className="p-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] ${c.env === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}
                    >
                      {c.env}
                    </span>
                  </td>
                  <td className="p-2 text-[11px] text-muted-foreground">
                    {c.last_used_at ? new Date(c.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="whitespace-nowrap p-2 text-right">
                    {c.status === 'revoked' ? (
                      <span className="text-[11px] text-muted-foreground">Revoked</span>
                    ) : (
                      <>
                        {/* Rotating changes one value, and the old secret keeps
                            working for 24h, so the integration can deploy the new
                            one without downtime. */}
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              setSecret({ client: await rotate.mutateAsync(c.id), rotated: true });
                            } catch (e) {
                              toast.error(message(e, 'Could not rotate'));
                            }
                          }}
                          title="Issue a new secret for this client ID"
                          className="mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px]"
                        >
                          <RotateCw className="h-3 w-3" /> Rotate
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevoking(c.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-red-600"
                        >
                          <Trash2 className="h-3 w-3" /> Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UsageTable tenantId={tenantId} locationId={locationId} timeZone={timeZone} />

      <ConfirmDialog
        open={!!revoking}
        title="Revoke this credential?"
        message="Their system stops working immediately, including tokens it already holds. This cannot be undone; issue a new credential if they need access again."
        confirmLabel="Revoke"
        loading={revoke.isPending}
        onConfirm={async () => {
          if (!revoking) return;
          try {
            await revoke.mutateAsync(revoking);
            toast.success('Credential revoked');
          } catch (e) {
            toast.error(message(e, 'Could not revoke'));
          }
          setRevoking(null);
        }}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}

// sent and delivered both mean it went out; delivered only once a receipt came.
const out = (c: { sent: number; delivered: number }) => c.sent + c.delivered;

// Messages per credential for one month, every outcome, from campaign-api's
// /admin/stats. Billing (live sends that went out) stays in the message-usage
// reports as type campaign_api.
function UsageTable({ tenantId, locationId, timeZone }: { tenantId: string; locationId: string; timeZone: string }) {
  const thisMonth = currentMonth(timeZone);
  const [month, setMonth] = useState(thisMonth);
  const { data, isLoading, error } = useCampaignApiStats(tenantId, locationId, month, timeZone);
  const rows = data?.data ?? [];

  const cells = (c: CampaignApiStatsCounts) => [
    out(c.sms),
    c.sms.failed,
    c.sms.suppressed,
    c.sms.segments,
    out(c.email),
    c.email.failed,
    c.email.suppressed,
    c.sms.queued + c.email.queued,
    c.batch_messages,
  ];

  return (
    <div className="rounded-md border border-border bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-2">
        <p className="flex items-center gap-1.5 text-xs font-medium">
          <Send className="h-3.5 w-3.5" /> Messages sent
        </p>
        <input
          type="month"
          aria-label="Month"
          value={month}
          max={thisMonth}
          onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="rounded-md border border-border px-2 py-1 text-xs"
        />
      </div>
      {isLoading ? (
        <p className="p-3 text-xs text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="p-3 text-xs text-red-700">{message(error, 'Could not load usage from Campaign API')}</p>
      ) : rows.length === 0 ? (
        <p className="p-3 text-center text-xs text-muted-foreground">No messages in this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="p-2 font-medium">Credential</th>
                <th className="p-2 text-right font-medium">SMS sent</th>
                <th className="p-2 text-right font-medium">SMS failed</th>
                <th className="p-2 text-right font-medium" title="Opted out: not sent">
                  SMS opted out
                </th>
                <th className="p-2 text-right font-medium" title="Billable SMS parts">
                  Segments
                </th>
                <th className="p-2 text-right font-medium">Email sent</th>
                <th className="p-2 text-right font-medium">Email failed</th>
                <th className="p-2 text-right font-medium" title="Opted out: not sent">
                  Email opted out
                </th>
                <th className="p-2 text-right font-medium" title="Waiting: quiet hours, pacing or retry">
                  Queued
                </th>
                <th className="p-2 text-right font-medium" title="Of these, sent through POST /messages/batch">
                  From batches
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client?.id ?? 'none'} className="border-b border-border last:border-0">
                  <td className="p-2">
                    <span className="font-medium">{r.client?.label ?? r.client?.client_id ?? '—'}</span>
                    {r.client?.env && (
                      <span
                        className={`ml-1.5 rounded px-1.5 py-0.5 text-[11px] ${r.client.env === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}
                      >
                        {r.client.env}
                      </span>
                    )}
                  </td>
                  {cells(r).map((n, i) => (
                    <td key={i} className="p-2 text-right tabular-nums">
                      {n}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length > 1 && data && (
                <tr className="border-t border-border bg-muted/30 font-medium">
                  <td className="p-2">Total</td>
                  {cells(data.totals).map((n, i) => (
                    <td key={i} className="p-2 text-right tabular-nums">
                      {n}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="border-t border-border p-2 text-[11px] text-muted-foreground">
        Months in store time ({timeZone}). Includes test credentials, which never send for real. Billing counts live
        sends only, in the message-usage reports as type <code>campaign_api</code>.
      </p>
    </div>
  );
}

// The secret exists in exactly one place for exactly one moment. Only a hash is
// stored, so it cannot be shown again — losing it means rotating.
function SecretOnce({
  client,
  rotated,
  onDismiss,
}: {
  client: CampaignApiClientWithSecret;
  rotated: boolean;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
      <div className="mb-2 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div>
          <p className="text-sm font-medium text-amber-900">Copy this secret now — it will not be shown again</p>
          <p className="text-[11px] text-amber-800">
            Only a hashed copy is stored. If it is lost, rotate the credential to get a new one.
            {rotated && ' The previous secret keeps working for 24 hours, then stops.'}
          </p>
        </div>
      </div>
      <div className="mb-2 space-y-1 rounded-md bg-white p-2 font-mono text-[11px]">
        <div>
          <span className="text-muted-foreground">client_id</span>
          <br />
          {client.client_id}
        </div>
        <div className="pt-1">
          <span className="text-muted-foreground">client_secret</span>
          <br />
          {client.client_secret}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(`client_id=${client.client_id}\nclient_secret=${client.client_secret}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy both'}
        </button>
        <button type="button" onClick={onDismiss} className="rounded-md border border-amber-300 px-3 py-1.5 text-xs">
          I have saved it
        </button>
      </div>
    </div>
  );
}
